import { estimateHistoryCommandBytes } from "./ComicCommands";
import {
  DEFAULT_HISTORY_LIMITS,
  type HistoryLimits,
  type HistoryState,
  type HistoryCommand,
} from "./historyTypes";

type BakeListener = (commands: ReadonlyArray<HistoryCommand>) => void;

export class HistoryManager {
  private applied: HistoryCommand[] = [];
  private redoStack: HistoryCommand[] = [];
  private totalPoints = 0;
  private estimatedBytes = 0;
  private wasTrimmed = false;

  public constructor(
    private readonly limits: Readonly<HistoryLimits> = DEFAULT_HISTORY_LIMITS,
    private readonly onBake: BakeListener = () => undefined,
  ) {}

  public add(command: HistoryCommand): void {
    this.applied.push(command);
    this.totalPoints += command.kind === "stroke" ? command.points.length : 0;
    this.estimatedBytes += estimateHistoryCommandBytes(command);
    this.redoStack = [];
    this.enforceLimits();
  }

  public undo(): boolean {
    const command = this.applied.pop();
    if (!command) return false;
    this.totalPoints -= command.kind === "stroke" ? command.points.length : 0;
    this.estimatedBytes -= estimateHistoryCommandBytes(command);
    this.redoStack.push(command);
    return true;
  }

  public redo(): boolean {
    const command = this.redoStack.pop();
    if (!command) return false;
    this.applied.push(command);
    this.totalPoints += command.kind === "stroke" ? command.points.length : 0;
    this.estimatedBytes += estimateHistoryCommandBytes(command);
    return true;
  }

  public clear(): void {
    this.applied = [];
    this.redoStack = [];
    this.totalPoints = 0;
    this.estimatedBytes = 0;
    this.wasTrimmed = false;
  }

  public getAppliedCommands(): ReadonlyArray<HistoryCommand> {
    return this.applied;
  }

  public getState(): HistoryState {
    return {
      canUndo: this.applied.length > 0,
      canRedo: this.redoStack.length > 0,
      commandCount: this.applied.length,
      pointCount: this.totalPoints,
      estimatedBytes: this.estimatedBytes,
      wasTrimmed: this.wasTrimmed,
    };
  }

  private enforceLimits(): void {
    const baked: HistoryCommand[] = [];
    while (
      // The producer bounds individual commands. Always retain the newest
      // command even if a custom or future producer supplies an oversized one,
      // so the action that just completed remains undoable as a single unit.
      this.applied.length > 1 &&
      (this.applied.length > this.limits.maxCommands ||
        this.totalPoints > this.limits.maxPoints ||
        this.estimatedBytes > this.limits.maxEstimatedBytes)
    ) {
      const command = this.applied.shift();
      if (!command) break;
      baked.push(command);
      this.totalPoints -= command.kind === "stroke" ? command.points.length : 0;
      this.estimatedBytes -= estimateHistoryCommandBytes(command);
    }

    if (baked.length > 0) {
      this.wasTrimmed = true;
      this.onBake(baked);
    }
  }
}
